"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import {
  CHAMPION_PLACEHOLDER_URL,
  getChampionImageUrl,
  getItemImageUrl,
  getSummonerSpellImageUrl,
} from "@/shared/lib/champion-image";
import { getRuneName, getRuneImageUrl } from "@/shared/config/runes";
import {
  getSummonerSpellEntry,
  getSummonerSpellName,
  getSummonerSpellImageKey,
} from "@/shared/config/summoner-spells";

import type { TournamentRulebookResponse } from "@/shared/api/tournament";

export interface RulebookDisplayCardsProps {
  rulebook: TournamentRulebookResponse | null | undefined;
  championNameById?: (id: number) => string | undefined;
  championKeyById?: (id: number) => string | undefined;
  locale?: string;
}

export function RulebookDisplayCards({
  rulebook,
  championNameById = () => undefined,
  championKeyById = () => undefined,
  locale = "ru",
}: RulebookDisplayCardsProps) {
  const t = useTranslations("Tournaments.rulebook");
  const tUi = useTranslations("Ui");

  const hasAny =
    rulebook &&
    (rulebook.timeoutMinutes != null ||
      rulebook.pausePerTeamMinutes != null ||
      rulebook.pausePerGameMinutes != null ||
      rulebook.pauseBetweenGamesMinutes != null ||
      rulebook.pauseRules ||
      rulebook.rehostRules ||
      (rulebook.bannedChampionIds?.length ?? 0) > 0 ||
      (rulebook.bannedRuneIds?.length ?? 0) > 0 ||
      (rulebook.bannedItemIds?.length ?? 0) > 0 ||
      (rulebook.bannedSummonerSpellIds?.length ?? 0) > 0 ||
      rulebook.defaultWinCondition != null ||
      rulebook.attachmentUrl);

  if (!hasAny) {
    return <p className="text-muted-foreground text-sm">{t("notConfigured")}</p>;
  }

  const rb = rulebook!;
  const hasTimeouts =
    rb.pausePerTeamMinutes != null ||
    rb.pausePerGameMinutes != null ||
    rb.pauseBetweenGamesMinutes != null ||
    rb.timeoutMinutes != null;
  const timeoutLine =
    hasTimeouts &&
    [
      rb.pausePerTeamMinutes != null && `${t("pausePerTeam")}: (${tUi("litRulebookDisplayCards.nMin", { minutes: rb.pausePerTeamMinutes })})`,
      rb.pausePerGameMinutes != null && `${t("pausePerGame")}: (${tUi("litRulebookDisplayCards.nMin", { minutes: rb.pausePerGameMinutes })})`,
      rb.pauseBetweenGamesMinutes != null && `${t("pauseBetweenGames")}: (${tUi("litRulebookDisplayCards.nMin", { minutes: rb.pauseBetweenGamesMinutes })})`,
      rb.timeoutMinutes != null && rb.pausePerTeamMinutes == null && rb.pausePerGameMinutes == null && rb.pauseBetweenGamesMinutes == null &&
        `${t("timeoutMinutes")}: (${tUi("litRulebookDisplayCards.nMin", { minutes: rb.timeoutMinutes })})`,
    ]
      .filter(Boolean)
      .join(" ");

  const subBlock = "rounded-2xl bg-muted/30 px-4 py-3 space-y-1";

  return (
    <div className="space-y-4">
      {rb.pauseRules && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("sectionRulesTitle")}</h4>
          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{rb.pauseRules}</p>
        </div>
      )}

      {rb.rehostRules && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("rehostRules")}</h4>
          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{rb.rehostRules}</p>
        </div>
      )}

      {timeoutLine && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("sectionTimeouts")}</h4>
          <p className="text-sm text-muted-foreground">{timeoutLine}</p>
        </div>
      )}

      {(rb.bannedChampionIds?.length ?? 0) > 0 && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("bannedChampionsLabel")}</h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {[...new Set(rb.bannedChampionIds!)].map((id) => {
              const name = championNameById(id);
              return (
                <div
                  key={id}
                  className="flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-1 text-sm"
                  title={name ?? String(id)}
                >
                  <Image
                    src={(championKeyById(id) && getChampionImageUrl(championKeyById(id)!)) || CHAMPION_PLACEHOLDER_URL}
                    alt={name ?? String(id)}
                    width={20}
                    height={20}
                    className="rounded object-cover"
                    unoptimized
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      const t = e.currentTarget;
                      if (t?.src !== CHAMPION_PLACEHOLDER_URL) t.src = CHAMPION_PLACEHOLDER_URL;
                    }}
                  />
                  <span className="font-medium text-foreground">{name ?? `ID ${id}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(rb.bannedRuneIds?.length ?? 0) > 0 && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("bannedRunesLabel")}</h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {rb.bannedRuneIds!.map((id) => {
              const iconUrl = getRuneImageUrl(id);
              return (
                <div
                  key={id}
                  className="flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-1 text-sm"
                  title={getRuneName(id, locale)}
                >
                  {iconUrl && (
                    <Image
                      src={iconUrl}
                      alt=""
                      width={18}
                      height={18}
                      className="rounded object-contain"
                      unoptimized
                    />
                  )}
                  <span className="font-medium text-foreground">{getRuneName(id, locale)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(rb.bannedItemIds?.length ?? 0) > 0 && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("bannedItemsLabel")}</h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {rb.bannedItemIds!.map((itemId) => (
              <div
                key={itemId}
                className="flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-1 text-sm"
                title={String(itemId)}
              >
                <Image
                  src={getItemImageUrl(itemId)}
                  alt=""
                  width={22}
                  height={22}
                  className="rounded object-contain"
                  unoptimized
                />
                <span className="font-medium text-foreground">{itemId}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(rb.bannedSummonerSpellIds?.length ?? 0) > 0 && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("bannedSummonerSpellsLabel")}</h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {rb.bannedSummonerSpellIds!.map((id) => {
              const entry = getSummonerSpellEntry(id);
              const iconUrl = entry ? getSummonerSpellImageUrl(getSummonerSpellImageKey(entry.key)) : null;
              return (
                <div
                  key={id}
                  className="flex items-center gap-1.5 rounded-md bg-muted/40 px-2 py-1 text-sm"
                  title={getSummonerSpellName(id, locale)}
                >
                  {iconUrl && (
                    <Image
                      src={iconUrl}
                      alt=""
                      width={18}
                      height={18}
                      className="rounded object-contain"
                      unoptimized
                    />
                  )}
                  <span className="font-medium text-foreground">{getSummonerSpellName(id, locale)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rb.defaultWinCondition != null && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("winConditionLabel")}</h4>
          <p className="text-sm text-muted-foreground">{t(`winCondition.${rb.defaultWinCondition}`)}</p>
        </div>
      )}

      {rb.attachmentUrl && (
        <div className={subBlock}>
          <h4 className="text-sm font-semibold text-foreground">{t("documentLabel")}</h4>
          <a
            href={rb.attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline text-sm"
          >
            {t("downloadRules")}
            {rb.attachmentFileName ? ` (${rb.attachmentFileName})` : ""}
          </a>
        </div>
      )}
    </div>
  );
}
